import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rtama_admin_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!token) {
        if (active) setUser(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setUser(data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        if (active) {
          setToken(null);
          setUser(false);
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [token]);

  /**
   * Step 1: send email + password.
   * Returns one of:
   *  - {ok: true} when (legacy) backend issues a final token directly
   *  - {requires_setup: true, setup_token, email}
   *  - {requires_2fa: true, challenge_token, email}
   */
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUser({ email: data.email, role: data.role });
      return { ok: true };
    }
    return data;
  };

  /** 2FA: get QR + secret from a setup_token. */
  const setup2FA = async (setup_token) => {
    const { data } = await axios.post(`${API}/auth/2fa/setup`, { setup_token });
    return data; // {secret, qr_code, otpauth_uri}
  };

  /** 2FA: verify the code during the initial enrollment. Issues final JWT. */
  const verifySetup = async (setup_token, code) => {
    const { data } = await axios.post(`${API}/auth/2fa/verify-setup`, {
      setup_token,
      code,
    });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser({ email: data.email, role: data.role });
    return data;
  };

  /** 2FA: verify code from a challenge_token (login from then on). */
  const verify2FA = async (challenge_token, code) => {
    const { data } = await axios.post(`${API}/auth/2fa/verify`, {
      challenge_token,
      code,
    });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser({ email: data.email, role: data.role });
    return data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, login, setup2FA, verifySetup, verify2FA, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
